import type { FC } from 'react'

import { LoaderCircle, SelectableCard } from '@skytech/ui'
import { LANGUAGE_FLAGS } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import CheckboxBlankCircleOutlineIcon from 'mdi-react/CheckboxBlankCircleOutlineIcon'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'

import { NoResults } from '@/components/NoResults'
import { useNewPolicyContext } from '@/modules/ecom-policy/new/index.context'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import type { Vendor as VendorType } from '@/modules/ecom-policy/types'

const StepVendor = () => {
  const newPolicy = useNewPolicyContext()
  const [selectedCustomerHeadId] = newPolicy.selectedCustomerHeadId
  const [selectedCustomerId] = newPolicy.selectedCustomerId

  const { data: customerData } = useQuery(
    queries.customerData(),
    ecomPolicyResources.getCustomerData
  )

  const customer = customerData?.customers.find(
    customer =>
      customer.id === selectedCustomerId || customer.customerHeadId === selectedCustomerHeadId
  )

  const selectedCustomerHeadIdParsed = customer?.customerHeadId

  const { data: vendorList, isLoading } = useQuery(
    queries.customerHeadVendorList(selectedCustomerHeadIdParsed!),
    () => ecomPolicyResources.getCustomerHeadVendorList(selectedCustomerHeadIdParsed!)
  )

  if (isLoading) {
    return <LoaderCircle />
  }

  if (!vendorList || vendorList.length === 0) {
    return <NoResults />
  }

  const customerSpecificPriceList = vendorList.filter(
    vendor => vendor.customerId === selectedCustomerId
  )
  const visibleVendorList =
    customerSpecificPriceList.length === 0 ? vendorList : customerSpecificPriceList
  const uniqueVendorList = uniqBy(visibleVendorList, 'id')

  return (
    <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {uniqueVendorList.map(vendor => (
        <Vendor key={vendor.id + '-' + vendor.ecomCustomerId} {...vendor} />
      ))}
    </div>
  )
}

const Vendor: FC<VendorType> = ({ id, name, locale, currency, ecomCustomerId }) => {
  const newPolicy = useNewPolicyContext()
  const [selectedVendorInfo, setSelectedVendorInfo] = newPolicy.selectedVendorInfo

  const isSelected = selectedVendorInfo.ecomCustomerId === ecomCustomerId

  return (
    <div className="m-2">
      <SelectableCard
        textLeft
        bordered
        padding={16}
        colorMode="yellow"
        isSelected={isSelected}
        onClick={() => setSelectedVendorInfo({ vendorId: id, ecomCustomerId: ecomCustomerId })}
      >
        <h3 className="mb-4 mt-1 text-2xl leading-none">{name}</h3>

        <p className="m-0 text-xl leading-5">
          <span className="mr-1">{LANGUAGE_FLAGS[locale]}</span>
          {currency}
        </p>

        <div className="checkmark absolute right-2 top-2">
          {isSelected ? (
            <CheckboxMarkedCircleOutlineIcon className="h-8 w-8" />
          ) : (
            <CheckboxBlankCircleOutlineIcon className="h-8 w-8" />
          )}
        </div>
      </SelectableCard>
    </div>
  )
}

export default StepVendor
